"use client";

import { useId, useState, type FormEvent } from "react";
import { StratosRichText } from "../StratosRichText/StratosRichText";
import type { IFormPanelProps } from "./FormPanel.props";

const controlClass =
  "min-h-12 w-full rounded-2xl border border-horizon-blue bg-mist/35 px-4 font-stratos-body text-base text-foreground outline-none transition-[background-color,border-color,box-shadow] placeholder:text-steel focus:border-primary focus:bg-cloud-white focus:ring-2 focus:ring-primary/15 aria-[invalid=true]:border-terracotta aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-terracotta/15";

export const FormPanel = ({
  errorMessage,
  fields,
  submitLabel,
  successMessage,
}: IFormPanelProps) => {
  const baseId = useId();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submissionFailed, setSubmissionFailed] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const controls = Array.from(
      form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
        "input, select, textarea",
      ),
    );
    const nextErrors: Record<string, string> = {};
    for (const control of controls)
      if (!control.checkValidity()) nextErrors[control.name] = control.validationMessage;
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      controls.find((control) => !control.checkValidity())?.focus();
      return;
    }
    setSubmitting(true);
    setSubmissionFailed(false);
    try {
      if (!navigator.onLine) throw new Error("offline");
      await new Promise<void>((resolve) => window.setTimeout(resolve, 350));
      setSucceeded(true);
    } catch {
      setSubmissionFailed(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (succeeded)
    return (
      <div
        aria-live="polite"
        className="rounded-[20px] bg-deep-violet p-7 text-cloud-white md:p-10"
        role="status"
      >
        <StratosRichText
          className="max-w-[65ch] text-cloud-white [&_h3]:font-stratos-display [&_h3]:text-3xl [&_h3]:font-bold"
          content={successMessage}
        />
      </div>
    );

  return (
    <form className="flex flex-col gap-6" noValidate onSubmit={submit}>
      {submissionFailed ? (
        <div className="rounded-2xl border border-terracotta bg-primary-100 p-5" role="alert">
          <StratosRichText className="text-base text-foreground" content={errorMessage} />
        </div>
      ) : null}
      {fields.map((field, index) => {
        const name = `field-${index}`;
        const inputId = `${baseId}-${index}`;
        const errorId = `${inputId}-error`;
        const error = errors[name];
        const shared = {
          "aria-describedby": error ? errorId : undefined,
          "aria-invalid": Boolean(error) as true | false,
          id: inputId,
          name,
          required: Boolean(field.required),
        };
        const label = (
          <span className="font-stratos-body text-xs font-medium">
            {field.label}
            {field.required ? <span className="ml-1 text-steel">(required)</span> : null}
          </span>
        );
        return (
          <div className="flex flex-col gap-2" key={`${field.label}-${index}`}>
            {field.fieldType === "checkbox" ? (
              <label className="flex min-h-11 cursor-pointer items-start gap-3">
                <input
                  {...shared}
                  className="size-5 shrink-0 border-horizon-blue accent-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  type="checkbox"
                />
                {label}
              </label>
            ) : (
              <>
                {<label htmlFor={inputId}>{label}</label>}
                {field.fieldType === "long-text" ? (
                  <textarea
                    {...shared}
                    className={`${controlClass} min-h-36 resize-y py-3`}
                    placeholder={field.placeholder || undefined}
                  />
                ) : field.fieldType === "select" ? (
                  <select {...shared} className={controlClass} defaultValue="">
                    <option disabled value="">
                      {field.placeholder}
                    </option>
                    {field.selectOptions?.map((option, optionIndex) => (
                      <option key={`${option.label}-${optionIndex}`} value={option.label}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    {...shared}
                    className={controlClass}
                    pattern={field.fieldType === "phone" ? "[+()0-9 .-]{7,}" : undefined}
                    placeholder={field.placeholder || undefined}
                    type={
                      field.fieldType === "email"
                        ? "email"
                        : field.fieldType === "phone"
                          ? "tel"
                          : "text"
                    }
                  />
                )}
              </>
            )}
            {error ? (
              <p className="font-stratos-body text-xs text-terracotta" id={errorId}>
                {error}
              </p>
            ) : null}
          </div>
        );
      })}
      <button
        className="inline-flex min-h-12 w-full items-center justify-center rounded-full border border-primary bg-primary px-7 font-stratos-body text-sm font-semibold text-primary-foreground transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-night-flight focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-wait disabled:opacity-65 sm:w-fit sm:min-w-44"
        disabled={submitting}
        type="submit"
      >
        {submitting ? `${submitLabel}…` : submitLabel}
      </button>
    </form>
  );
};

FormPanel.displayName = "Content::FormPanel";
